'use server';

import { createClient, createAdminClient } from '@/lib/supabase/server';
import { revalidatePath } from 'next/cache';
import type { ItemRow } from '@/lib/inventory25/identity';
import { expandKioskBundle, kioskBundleIncludeNames, type KioskBundleLine } from '@/lib/inventory25/kioskBundle';
import { escapeIlikeFragment } from '@/lib/searchEscape';

const STUDENT_PATHS = ['/student/browse', '/student/cart'];

// deleteInventoryItem/updateItem/addItem/updateRepairStatus/updateTether/
// reorderKitItems/createSubKit/getKioskItems/addKioskItem/removeKioskItem/
// isKioskEligible/getAddonItems/addAddonItem/removeAddonItem/
// getKioskItemDetails/getAvailabilityForDateRange — all removed 2026-08-18.
// Confirmed zero remaining callers anywhere in the app (the V1/V2 edit UI that
// used to call them — EditItemModal.tsx, InventoryV2EditModal.tsx — was already
// deleted separately) and items_v1_legacy is fully dropped from the live DB.
// Inventory 2.5 equivalents live in src/actions/inventory25staff.ts (item CRUD),
// this file's "Inventory 2.5" sections below (kiosk/addon), and
// src/actions/inventory25availability.ts (availability).

export async function addConditionLog(
  barcode: string,
  condition: string,
  note: string,
  loggedByName: string,
  loggedById: string
) {
  const supabase = await createClient();
  const { error } = await supabase.from('condition_log').insert({
    barcode,
    condition,
    note,
    logged_by: loggedById,
    logged_by_name: loggedByName,
  });
  if (error) throw new Error(error.message);
  revalidatePath(`/staff/inventory/${barcode}`);
}

// updateTether/reorderKitItems/createSubKit/getKioskItems/addKioskItem/
// removeKioskItem/isKioskEligible (all V1/V2 barcode-keyed) removed 2026-08-18 —
// zero remaining callers (staff/admin's KioskManager was updated in the same pass
// to use only the item_id-keyed *25 functions below; it previously called
// getKioskItems() inside a Promise.all alongside getKiosk25Items(), and since
// items_v1_legacy is fully dropped, getKioskItems()'s embed threw on every load —
// rejecting the whole Promise.all and silently emptying BOTH lists, not just the
// legacy one. That's fixed as part of this cleanup, not a pre-existing bug left in
// place.) kiosk_items gained `id` (surrogate PK) and `item_id` in
// 2_5_025_kiosk_items_item_id.sql, not yet reflected in the generated Database type
// (regenerating it app-wide is inventory-foundation's Phase 7 job, per
// src/lib/supabase/inventory25.ts's header comment on the same class of gap) — cast
// the query builder itself rather than leave `.is('item_id', ...)`/`.eq('item_id', ...)`
// unable to type-check against a column the stale type doesn't know about yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function kioskItemsTable(supabase: Awaited<ReturnType<typeof createClient>>): any {
  return supabase.from('kiosk_items');
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function kioskItemsTableAdmin(supabase: Awaited<ReturnType<typeof createAdminClient>>): any {
  return supabase.from('kiosk_items');
}

// ── Kiosk-eligible items (Inventory 2.5, item_id-keyed) ────────────────────────
//
// Sibling of the block above, added once 2_5_025_kiosk_items_item_id.sql makes
// item_id a valid second identity on kiosk_items. Deliberately separate functions
// rather than widening addKioskItem/removeKioskItem/getKioskItems in place — those
// three stay barcode-only for legacy V1 rows. Inventory 2.5 uses the *25*
// siblings below; staff entry points are Inspector Advanced, Family Inspector,
// list/structure bulk Select, and Admin KioskManager (list + remove for 2.5).

export interface Kiosk25Entry {
  itemId: string;
  displayName: string;
  /** Variant / Unit ID when present (e.g. A, Turtle, Rocky). */
  unitLabel: string | null;
  familyId: string | null;
  familyName: string | null;
  defaultDurationMinutes: number;
  createdAt: string;
}

export async function getKiosk25Items(): Promise<Kiosk25Entry[]> {
  const supabase = await createClient();
  const { data, error } = await kioskItemsTable(supabase)
    .select('item_id, default_duration_minutes, created_at, items(display_name, unit_label)')
    .not('item_id', 'is', null)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = (data ?? []) as any[];
  const itemIds = rows.map((r) => r.item_id as string).filter(Boolean);

  const familyByItemId = new Map<string, { familyId: string; familyName: string }>();
  if (itemIds.length > 0) {
    // family_members may lag generated Database types — same cast pattern as kioskItemsTable.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: memberships, error: memErr } = await (supabase.from('family_members') as any)
      .select('item_id, family_id, families(id, name)')
      .in('item_id', itemIds);
    if (memErr) throw new Error(memErr.message);
    for (const m of (memberships ?? []) as {
      item_id: string;
      family_id: string;
      families: { id: string; name: string } | null;
    }[]) {
      if (!m.families) continue;
      familyByItemId.set(m.item_id, { familyId: m.family_id, familyName: m.families.name });
    }
  }

  return rows.map((row) => {
    const item = row.items as { display_name: string; unit_label: string | null } | null;
    const fam = familyByItemId.get(row.item_id as string);
    return {
      itemId: row.item_id as string,
      defaultDurationMinutes: row.default_duration_minutes as number,
      createdAt: row.created_at as string,
      displayName: item?.display_name ?? (row.item_id as string),
      unitLabel: item?.unit_label ?? null,
      familyId: fam?.familyId ?? null,
      familyName: fam?.familyName ?? null,
    };
  });
}

export async function addKiosk25Item(itemId: string, defaultDurationMinutes = 60, addedBy: string) {
  const supabase = await createClient();
  const { error } = await kioskItemsTable(supabase).upsert(
    { item_id: itemId, default_duration_minutes: defaultDurationMinutes, added_by: addedBy },
    { onConflict: 'item_id' }
  );
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
  revalidatePath('/staff/inventory');
  revalidatePath('/staff/inventory/structure');
  revalidatePath('/kiosk');
}

/** Resolve Inventory 2.5 by barcode (or UUID) and add to the kiosk allowlist. */
export async function addKiosk25ItemByBarcode(
  identity: string,
  defaultDurationMinutes = 60,
  addedBy: string
) {
  const trimmed = identity.trim();
  if (!trimmed) throw new Error('Enter a barcode or item id.');

  const items = await items25Admin();
  const looksUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
  let query = items.select('id').eq('is_retired', false).limit(1);
  query = looksUuid ? query.eq('id', trimmed) : query.eq('barcode', trimmed);
  const { data: row, error: findErr } = await query.maybeSingle();
  if (findErr) throw new Error(findErr.message);
  if (!row) throw new Error(`No Inventory 2.5 item found for "${trimmed}". Legacy barcodes cannot be added here.`);

  await addKiosk25Item((row as { id: string }).id, defaultDurationMinutes, addedBy);
}

export async function removeKiosk25Item(itemId: string) {
  const supabase = await createClient();
  const { error } = await kioskItemsTable(supabase).delete().eq('item_id', itemId);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
  revalidatePath('/staff/inventory');
  revalidatePath('/staff/inventory/structure');
  revalidatePath('/kiosk');
}

/** Bulk add Inventory 2.5 items to the kiosk allowlist. Folders are allowed —
 *  scanning one at the kiosk checks out everything inside. Returns how many were written. */
export async function bulkAddKiosk25Items(
  itemIds: string[],
  defaultDurationMinutes = 60,
  addedBy: string
): Promise<{ added: number; skipped: number }> {
  const unique = [...new Set(itemIds.filter(Boolean))];
  if (unique.length === 0) return { added: 0, skipped: 0 };

  const supabase = await createClient();
  const items = await items25Admin();
  const { data: rows } = await items.select('id').in('id', unique);
  const eligibleIds = ((rows ?? []) as { id: string }[]).map(r => r.id);
  const skipped = unique.length - eligibleIds.length;

  for (const id of eligibleIds) {
    const { error } = await kioskItemsTable(supabase).upsert(
      { item_id: id, default_duration_minutes: defaultDurationMinutes, added_by: addedBy },
      { onConflict: 'item_id' }
    );
    if (error) throw new Error(error.message);
  }

  revalidatePath('/staff/admin');
  revalidatePath('/staff/inventory');
  revalidatePath('/staff/inventory/structure');
  revalidatePath('/kiosk');
  return { added: eligibleIds.length, skipped };
}

/** Bulk remove Inventory 2.5 items from the kiosk allowlist. */
export async function bulkRemoveKiosk25Items(itemIds: string[]): Promise<number> {
  const unique = [...new Set(itemIds.filter(Boolean))];
  if (unique.length === 0) return 0;

  const supabase = await createClient();
  const { error, count } = await kioskItemsTable(supabase)
    .delete({ count: 'exact' })
    .in('item_id', unique);
  if (error) throw new Error(error.message);

  revalidatePath('/staff/admin');
  revalidatePath('/staff/inventory');
  revalidatePath('/staff/inventory/structure');
  revalidatePath('/kiosk');
  return count ?? unique.length;
}

export interface Kiosk25CatalogResult {
  itemId: string;
  displayName: string;
  unitLabel: string | null;
  barcode: string | null;
  familyId: string | null;
  familyName: string | null;
  alreadyEligible: boolean;
}

/** Search the Inventory 2.5 catalog by item name or family name — for admin's
 *  KioskManager item/family picker (replaces raw-barcode-only entry; that input
 *  is kept as a fallback since USB scanners still fill it directly). Matches on
 *  either the item's own display_name or its family's name so typing a family
 *  ("Rocky") surfaces every member even if none of their individual names match. */
export async function searchKiosk25CatalogItems(query: string): Promise<Kiosk25CatalogResult[]> {
  const q = escapeIlikeFragment(query);
  if (q.length < 2) return [];

  const supabase = await createClient();
  const items = await items25Admin();

  const { data: famMatches } = await supabase
    .from('families')
    .select('id')
    .ilike('name', `%${q}%`)
    .limit(10);
  const familyIds = ((famMatches ?? []) as { id: string }[]).map((f) => f.id);

  let itemIdsFromFamily: string[] = [];
  if (familyIds.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: fm } = await (supabase.from('family_members') as any)
      .select('item_id')
      .in('family_id', familyIds);
    itemIdsFromFamily = ((fm ?? []) as { item_id: string }[]).map((r) => r.item_id);
  }

  let itemsQuery = items.select('id, display_name, unit_label, barcode').eq('is_retired', false);
  itemsQuery = itemIdsFromFamily.length > 0
    ? itemsQuery.or(`display_name.ilike.%${q}%,id.in.(${itemIdsFromFamily.join(',')})`)
    : itemsQuery.ilike('display_name', `%${q}%`);
  const { data, error } = await itemsQuery.order('display_name', { ascending: true }).limit(50);
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as { id: string; display_name: string; unit_label: string | null; barcode: string | null }[];
  const itemIds = rows.map((r) => r.id);

  const familyByItemId = new Map<string, { familyId: string; familyName: string }>();
  if (itemIds.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: memberships } = await (supabase.from('family_members') as any)
      .select('item_id, family_id, families(id, name)')
      .in('item_id', itemIds);
    for (const m of (memberships ?? []) as {
      item_id: string;
      family_id: string;
      families: { id: string; name: string } | null;
    }[]) {
      if (!m.families) continue;
      familyByItemId.set(m.item_id, { familyId: m.family_id, familyName: m.families.name });
    }
  }

  const { data: eligibleRows } = await kioskItemsTable(supabase)
    .select('item_id')
    .not('item_id', 'is', null);
  const eligibleSet = new Set(((eligibleRows ?? []) as { item_id: string }[]).map((r) => r.item_id));

  return rows.map((r) => {
    const fam = familyByItemId.get(r.id);
    return {
      itemId: r.id,
      displayName: r.display_name,
      unitLabel: r.unit_label,
      barcode: r.barcode,
      familyId: fam?.familyId ?? null,
      familyName: fam?.familyName ?? null,
      alreadyEligible: eligibleSet.has(r.id),
    };
  });
}

// ── Add-on pool (Inventory 2.5 — items students may append to packages) ───────

export interface AddonItem25 {
  id: string;
  barcode: string | null;
  itemName: string;
  unitLabel: string | null;
  familyId: string | null;
  familyName: string | null;
}

export async function getAddonItems25(): Promise<AddonItem25[]> {
  const supabase = await createClient();
  const items = await items25Admin();
  const { data, error } = await items
    .select('id, barcode, display_name, unit_label')
    .eq('is_addon_eligible', true)
    .eq('is_retired', false)
    .order('display_name', { ascending: true });
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as {
    id: string;
    barcode: string | null;
    display_name: string;
    unit_label: string | null;
  }[];
  const itemIds = rows.map((r) => r.id);

  const familyByItemId = new Map<string, { familyId: string; familyName: string }>();
  if (itemIds.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: memberships, error: memErr } = await (supabase.from('family_members') as any)
      .select('item_id, family_id, families(id, name)')
      .in('item_id', itemIds);
    if (memErr) throw new Error(memErr.message);
    for (const m of (memberships ?? []) as {
      item_id: string;
      family_id: string;
      families: { id: string; name: string } | null;
    }[]) {
      if (!m.families) continue;
      familyByItemId.set(m.item_id, { familyId: m.family_id, familyName: m.families.name });
    }
  }

  return rows.map((r) => {
    const fam = familyByItemId.get(r.id);
    return {
      id: r.id,
      barcode: r.barcode,
      itemName: r.display_name,
      unitLabel: r.unit_label ?? null,
      familyId: fam?.familyId ?? null,
      familyName: fam?.familyName ?? null,
    };
  });
}

/** Resolve by barcode (preferred) or item UUID and mark add-on eligible. */
export async function addAddonItem25(identity: string) {
  const items = await items25Admin();
  const trimmed = identity.trim();
  if (!trimmed) throw new Error('Enter a barcode or item id.');

  let query = items.select('id').eq('is_retired', false).limit(1);
  const looksUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
  query = looksUuid ? query.eq('id', trimmed) : query.eq('barcode', trimmed);
  const { data: row, error: findErr } = await query.maybeSingle();
  if (findErr) throw new Error(findErr.message);
  if (!row) throw new Error(`No Inventory 2.5 item found for "${trimmed}".`);

  const { error } = await items
    .update({ is_addon_eligible: true, updated_at: new Date().toISOString() })
    .eq('id', (row as { id: string }).id);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
  STUDENT_PATHS.forEach((p) => revalidatePath(p));
}

export async function removeAddonItem25(itemId: string) {
  const items = await items25Admin();
  const { error } = await items
    .update({ is_addon_eligible: false, updated_at: new Date().toISOString() })
    .eq('id', itemId);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
  STUDENT_PATHS.forEach((p) => revalidatePath(p));
}

/** Distinct manufacturer / acquisition values already on Inventory 2.5 items. */
export async function getInventory25FieldDistinct(
  field: 'manufacturer' | 'acquisition_method' | 'purchase_source'
): Promise<string[]> {
  const items = await items25Admin();
  const { data, error } = await items.select(field).eq('is_retired', false).not(field, 'is', null);
  if (error) throw new Error(error.message);
  const set = new Set<string>();
  for (const row of (data ?? []) as Record<string, string | null>[]) {
    const v = (row[field] ?? '').trim();
    if (v) set.add(v);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

// Legacy V1 addon helpers (getAddonItems/addAddonItem/removeAddonItem, AddonItem
// type) removed 2026-08-18 — zero remaining callers, items_v1_legacy fully dropped.
// Use the *25 equivalents above.

// ── Kiosk server actions (unauthenticated browser — must use admin client) ────
//
// items25Admin() mirrors src/lib/supabase/inventory25.ts's table25() workaround
// (2.5's `items` table isn't in the generated Database type yet — see that file's
// header) but can't reuse table25() itself: table25() calls the session-bound
// createClient(), and kiosk visitors have no Supabase auth session at all — every
// kiosk action in this file already has to use createAdminClient() for exactly that
// reason (see this section's own header above).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function items25Admin(): Promise<any> {
  const supabase = await createAdminClient();
  return supabase.from('items');
}

export interface KioskSearchResult {
  barcode: string | null;
  itemId: string | null;
  itemName: string;
  availableCount: number;
  // De-dup key for kiosk/page.tsx's name-search grouping — legacy rows group by
  // unit_group_id (same physical item type, many interchangeable barcodes) the way
  // they always have; 2.5 rows group by their own item_id since kiosk deliberately
  // doesn't reimplement Family/rotation-pool resolution (checkoutUnit.ts) for this
  // convenience search — students wanting pool behavior use Browse/Cart instead.
  groupKey: string;
}

const KIOSK_SEARCH_ITEM_SELECT = 'item_id, items!inner(display_name, qty_available, barcode, unit_label, category_id)';

export async function searchKioskItemsByName(query: string): Promise<KioskSearchResult[]> {
  const q = escapeIlikeFragment(query);
  if (!q) return [];
  const pattern = `%${q}%`;
  const supabase = await createAdminClient();

  const [{ data: byName, error }, { data: byBarcode }, { data: byLabel }, { data: categoryRows }] = await Promise.all([
    kioskItemsTableAdmin(supabase).select(KIOSK_SEARCH_ITEM_SELECT).not('item_id', 'is', null).ilike('items.display_name', pattern).limit(30),
    kioskItemsTableAdmin(supabase).select(KIOSK_SEARCH_ITEM_SELECT).not('item_id', 'is', null).ilike('items.barcode', pattern).limit(30),
    kioskItemsTableAdmin(supabase).select(KIOSK_SEARCH_ITEM_SELECT).not('item_id', 'is', null).ilike('items.unit_label', pattern).limit(30),
    supabase.from('categories').select('id, name').ilike('name', pattern),
  ]);
  if (error) throw new Error(error.message);

  const matchingCategoryIds = ((categoryRows ?? []) as { id: string }[]).map((c) => c.id);
  const { data: byCategory } = matchingCategoryIds.length > 0
    ? await kioskItemsTableAdmin(supabase)
        .select(KIOSK_SEARCH_ITEM_SELECT)
        .not('item_id', 'is', null)
        .in('items.category_id', matchingCategoryIds)
        .limit(30)
    : { data: [] };

  const seen = new Set<string>();
  const results: KioskSearchResult[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const r of [...((byName ?? []) as any[]), ...((byBarcode ?? []) as any[]), ...((byLabel ?? []) as any[]), ...((byCategory ?? []) as any[])]) {
    const itemId = r.item_id as string;
    if (!itemId || seen.has(itemId)) continue;
    seen.add(itemId);
    results.push({
      barcode: (r.items as { barcode: string | null }).barcode ?? null,
      itemId,
      itemName: (r.items as { display_name: string }).display_name,
      availableCount: (r.items as { qty_available: number | null }).qty_available ?? 0,
      groupKey: itemId,
    });
  }
  return results;
}

// getKioskItemDetails (V1 barcode lookup) removed 2026-08-18 — zero remaining
// callers, superseded by getKiosk25ItemDetails below.

export interface Kiosk25ItemDetails {
  itemId: string;
  displayName: string;
  role: ItemRow['role'];
  qtyAvailable: number;
  requestPolicy: string | null;
  eligible: boolean;
  defaultDurationMinutes: number;
  /** Names of items that travel with this scan (folder contents / attached kids). */
  includes: string[];
}

// Looks up a 2.5 item by either identity: `barcode` (an optional scan alias, never
// the join key — see src/lib/inventory25/identity.ts) for the kiosk's raw
// scan/type-in flow, or `itemId` directly when the caller already resolved it (the
// name-search dropdown already has item_id from searchKioskItemsByName, so it skips
// straight to that branch instead of re-deriving identity from a barcode that may not
// even exist on the item). Folders/Sets/Assets with attachments are allowed — scanning
// them expands into concrete checkout lines via expandKiosk25CheckoutLines.
export async function getKiosk25ItemDetails(
  identity: { barcode?: string | null; itemId?: string | null }
): Promise<Kiosk25ItemDetails | null> {
  const items = await items25Admin();
  let query = items.select('*').limit(1);
  query = identity.itemId ? query.eq('id', identity.itemId) : query.eq('barcode', identity.barcode);
  const { data: item } = await query.maybeSingle();
  if (!item) return null;
  const root = item as ItemRow;

  const { data: allRows } = await items.select('*').eq('is_retired', false);
  const allItems = (allRows ?? []) as ItemRow[];
  const includes = kioskBundleIncludeNames(root, allItems);
  const expanded = expandKioskBundle(root, allItems, 0);

  const supabase = await createAdminClient();
  const { data: ki } = await kioskItemsTableAdmin(supabase)
    .select('default_duration_minutes')
    .eq('item_id', root.id)
    .maybeSingle();

  // Folders/Sets have no stock of their own — treat as available when the bundle
  // expands to at least one concrete line. Units/supplies use their own qty.
  let qtyAvailable = root.qty_available ?? 0;
  if (root.role === 'folder' || root.role === 'set') {
    qtyAvailable = expanded.length > 0 ? 1 : 0;
  }

  return {
    itemId: root.id,
    displayName: root.display_name,
    role: root.role,
    qtyAvailable,
    requestPolicy: root.request_policy ?? null,
    eligible: ki !== null,
    defaultDurationMinutes: (ki as { default_duration_minutes: number } | null)?.default_duration_minutes ?? 60,
    includes,
  };
}

/** Expand a kiosk-scanned 2.5 item into the concrete checkout_items rows that leave
 *  the cage (folder contents, set members, attached children). */
export async function expandKiosk25CheckoutLines(
  itemIds: string[]
): Promise<KioskBundleLine[]> {
  const unique = [...new Set(itemIds.filter(Boolean))];
  if (unique.length === 0) return [];

  const items = await items25Admin();
  const { data: allRows, error } = await items.select('*').eq('is_retired', false);
  if (error) throw new Error(error.message);
  const allItems = (allRows ?? []) as ItemRow[];
  const byId = new Map(allItems.map((i) => [i.id, i]));

  const stamp = Date.now();
  const out: KioskBundleLine[] = [];
  const seen = new Set<string>();
  for (const id of unique) {
    const root = byId.get(id);
    if (!root) continue;
    for (const line of expandKioskBundle(root, allItems, stamp)) {
      if (seen.has(line.itemId)) continue;
      seen.add(line.itemId);
      out.push(line);
    }
  }
  return out;
}

/** Outcome of scanning an item for a student who may already have it out. */
export type KioskScanLoan =
  | { kind: 'none' }
  | {
      kind: 'kiosk_return';
      lines: { checkoutId: string; itemId: string | null; barcode: string | null; itemName: string }[];
    }
  /** Item (or something in its bundle) is on a staff-desk checkout — kiosk must not touch it. */
  | { kind: 'staff_desk'; itemName: string };

/**
 * Decide whether a kiosk scan should check out, return (kiosk-only), or refuse.
 * Staff-desk checkouts are never returnable at the kiosk — catch that here so the
 * cart never fills with a return that will fail (or wrongly close) at Confirm.
 */
export async function resolveKioskScanLoan(
  identity: { barcode?: string | null; itemId?: string | null },
  studentId: string
): Promise<KioskScanLoan> {
  const supabase = await createAdminClient();

  // Resolve 2.5 root → leaf item_ids (folder/set/attached expansion).
  let leafIds: string[] = [];
  let rootName = 'This item';
  let rootBarcode: string | null = identity.barcode ?? null;
  if (identity.itemId || identity.barcode) {
    const items = await items25Admin();
    let q = items.select('*').limit(1);
    q = identity.itemId ? q.eq('id', identity.itemId) : q.eq('barcode', identity.barcode);
    const { data: rootRow } = await q.maybeSingle();
    if (rootRow) {
      const root = rootRow as ItemRow;
      rootName = root.display_name;
      rootBarcode = root.barcode ?? rootBarcode;
      const { data: allRows } = await items.select('*').eq('is_retired', false);
      leafIds = expandKioskBundle(root, (allRows ?? []) as ItemRow[], 0).map((l) => l.itemId);
      if (leafIds.length === 0) leafIds = [root.id];
    }
  }

  type LoanRow = {
    checkout_id: string;
    item_id: string | null;
    barcode: string | null;
    item_name: string;
    checkouts: { student_id: string; status: string; source: string | null };
  };

  let rows: LoanRow[] = [];

  if (leafIds.length > 0) {
    const { data } = await supabase
      .from('checkout_items')
      .select('checkout_id, item_id, barcode, item_name, checkouts!inner(student_id, status, source)')
      .in('item_id', leafIds)
      .eq('checkouts.student_id', studentId)
      .eq('checkouts.status', 'active')
      .is('returned_at', null);
    rows = (data ?? []) as unknown as LoanRow[];
  }

  // Barcode match on active kiosk lines (covers older rows that stored barcode but
  // null item_id — Inventory 2.5 is still the catalog; we don't touch V1).
  if (rows.length === 0 && rootBarcode) {
    const { data } = await supabase
      .from('checkout_items')
      .select('checkout_id, item_id, barcode, item_name, checkouts!inner(student_id, status, source)')
      .eq('barcode', rootBarcode)
      .eq('checkouts.student_id', studentId)
      .eq('checkouts.status', 'active')
      .is('returned_at', null);
    rows = (data ?? []) as unknown as LoanRow[];
    if (rows[0]?.item_name) rootName = rows[0].item_name;
  }

  if (rows.length === 0) return { kind: 'none' };

  const hasStaff = rows.some((r) => (r.checkouts?.source ?? 'staff') !== 'kiosk');
  if (hasStaff) {
    return { kind: 'staff_desk', itemName: rootName };
  }

  return {
    kind: 'kiosk_return',
    lines: rows.map((r) => ({
      checkoutId: r.checkout_id,
      itemId: r.item_id,
      barcode: r.barcode,
      itemName: r.item_name,
    })),
  };
}

/** @deprecated Prefer resolveKioskScanLoan — kept for any external callers. */
export async function getKioskActiveBundleReturn(
  identity: { barcode?: string | null; itemId?: string | null },
  studentId: string
): Promise<{ checkoutId: string; itemId: string | null; barcode: string | null; itemName: string }[] | null> {
  const loan = await resolveKioskScanLoan(identity, studentId);
  if (loan.kind !== 'kiosk_return') return null;
  return loan.lines;
}

export async function getKioskActiveCheckoutItem(
  identity: { barcode?: string | null; itemId?: string | null },
  studentId: string
) {
  const loan = await resolveKioskScanLoan(identity, studentId);
  if (loan.kind !== 'kiosk_return' || loan.lines.length === 0) return null;
  return { checkout_id: loan.lines[0].checkoutId };
}

export async function getCheckoutUnreturnedCount(checkoutId: string): Promise<number> {
  const supabase = await createAdminClient();
  const { count } = await supabase
    .from('checkout_items')
    .select('*', { count: 'exact', head: true })
    .eq('checkout_id', checkoutId)
    .is('returned_at', null);
  return count ?? 0;
}

